/**
 * The SPA's runtime configuration (PLAN §10.2): `GET /config.json`, fetched
 * by the SPA at boot so ONE build runs anywhere — served by CloudFront (the
 * stage's cloud API, written by `infrastructure/web.tf` from the stack's own
 * values) or by a node's state service (the node itself).
 *
 *     { backend: { kind, label, apiUrl, wsUrl }, auth: cognito | dev,
 *       status?: { cloud: { apiUrl, wsUrl } | null, nodes: [{ label, apiUrl }] } }
 *
 * `apiUrl` and `wsUrl` are absolute, or relative to the page's origin: `""`
 * is the same origin, `"/ws"` resolves to `ws(s)://<host>/ws`. A node answers
 * relative ones, so the config is right under whatever name the node is
 * reached by (a LAN IP, its D16 name, localhost).
 *
 * STRICT, and shared with the SPA (`apps/web` validates the same shape): a key
 * is added here and in both producers in one change.
 *
 * `status` is PLAN §10.2's multi-connection model: every endpoint the SPA
 * watches besides (and including) the one that served it — the stage's cloud
 * API (null when a node runs with no cloud) and the site's nodes (the serving
 * node itself, as `""`, and its peer). The connectivity panel probes each
 * one's `GET /health`, and the SPA picks its primary backend among them. A
 * node's WS endpoint is its `apiUrl` + `/ws` (the node's own layout), so it
 * is not repeated here. OPTIONAL, for exactly one reason: the SPA must keep
 * reading a `/config.json` written before the block existed (CloudFront's is
 * written by OpenTofu on its own schedule, `web_config_status_block` in
 * `infrastructure/web.tf`); without it the SPA watches only `backend`.
 */
import { z } from 'zod';

/** An absolute URL of the given schemes, or a path relative to the page's origin (`""`: the origin itself). */
function endpoint(schemes: readonly string[]) {
  return z.string().refine((value) => value === '' || value.startsWith('/') || schemes.some((scheme) => value.startsWith(`${scheme}://`)), {
    message: `expected "", a path starting with "/", or a ${schemes.join('/')} URL`,
  });
}

export const runtimeBackendSchema = z
  .object({
    /** Who serves this page's data path: the stage's cloud API, or an on-prem node. */
    kind: z.enum(['cloud', 'node']),
    /** What the SPA calls this backend (the stage, or the node's name). */
    label: z.string().min(1).max(128),
    apiUrl: endpoint(['https', 'http']),
    wsUrl: endpoint(['wss', 'ws']),
  })
  .strict();

export const runtimeAuthSchema = z.discriminatedUnion('kind', [
  /** Sign in against the stage's Cognito pool with SRP (D5), as the `-spa` app client. */
  z.object({ kind: z.literal('cognito'), region: z.string().min(1), userPoolId: z.string().min(1), clientId: z.string().min(1) }).strict(),
  /** A development node: `POST /dev/token` mints the token (loopback only, `ACP_DEV_AUTH`). */
  z.object({ kind: z.literal('dev') }).strict(),
]);

export const runtimeStatusSchema = z
  .object({
    /** The stage's cloud API — the status connection of a node-served page, the page's own backend on CloudFront. */
    cloud: z.object({ apiUrl: endpoint(['https', 'http']), wsUrl: endpoint(['wss', 'ws']) }).strict().nullable(),
    /** The site's nodes, in preference order (the serving node first). At most two today: main and backup (§8). */
    nodes: z
      .array(z.object({ label: z.string().min(1).max(128), apiUrl: endpoint(['https', 'http']) }).strict())
      .max(8),
  })
  .strict();

export const runtimeConfigSchema = z.object({ backend: runtimeBackendSchema, auth: runtimeAuthSchema, status: runtimeStatusSchema.optional() }).strict();

export type RuntimeConfig = z.infer<typeof runtimeConfigSchema>;
export type RuntimeAuth = z.infer<typeof runtimeAuthSchema>;
export type RuntimeStatus = z.infer<typeof runtimeStatusSchema>;
